import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Table, Button, Form, Row, Col, Alert, Spinner, Placeholder } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faPencil, faCheck, faXmark, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { getCatalogItems, getCatalogMetrics, updateCatalogRoLevel } from '../services/api';
import useDebounce from '../hooks/useDebounce';
import CatalogItemModal from './CatalogItemModal';

// ─── Metric cell helpers ────────────────────────────────────────────────────

const MetricSkeleton = () => (
  <Placeholder as="span" animation="glow">
    <Placeholder xs={8} />
  </Placeholder>
);

// Annualized turns/year coloring
// < 1 turn/yr = low (red), 1–4 = moderate (yellow), > 4 = good (green)
function turnoverColor(turns) {
  if (turns === null || turns === undefined) return 'transparent';
  if (turns >= 4) return '#e6f4ea';
  if (turns >= 1) return '#fff4e6';
  return '#ffe6e6';
}

// Sell-through: < 20% = red, 20–50% = yellow, > 50% = green
function sellThroughColor(rate) {
  if (rate === null || rate === undefined) return 'transparent';
  const pct = rate * 100;
  if (pct >= 50) return '#e6f4ea';
  if (pct >= 20) return '#fff4e6';
  return '#ffe6e6';
}

// Days of supply: < 30 = red, 30–60 = yellow, > 60 = green
// 0 is real (demand but nothing available) and shows red; null means no demand.
function daysOfSupplyColor(days) {
  if (days == null) return 'transparent';
  if (days < 30) return '#ffe6e6';
  if (days < 60) return '#fff4e6';
  return '#e6f4ea';
}

// Weighted avg age: > 180d = red, 90–180d = yellow, < 90d = ok
function avgAgeColor(days) {
  if (!days) return 'transparent';
  if (days > 180) return '#ffe6e6';
  if (days > 90)  return '#fff4e6';
  return 'transparent';
}

// Recommended reorder point: ~30 days of average daily demand
function computeRecommendedRo(m) {
  if (!m || m === 'loading' || m === 'error') return null;
  // Suppress recommendation when there isn't enough order history to be reliable
  if (getConfidenceLevel(m) === 'low') return null;
  const daily = Number(m.issued_90_days) / 90;
  if (!daily) return null;
  return Math.round(daily * 30);
}

/**
 * Confidence tiers for derived velocity metrics (turns, sell-through, DOS, etc.)
 *
 * Based on distinct issue transactions in the last 90 days plus lifetime context:
 *   high       : ≥3 orders in 90d  — solid statistical basis
 *   moderate   : 1–2 orders in 90d — directionally useful, treat with caution
 *   low        : 0 orders in 90d   — insufficient to rely on derived metrics
 *
 * The lifetime_order_count distinguishes a truly sparse product (never active)
 * from one that is recently quiet (historically active).
 */
function getConfidenceLevel(m) {
  if (!m || m === 'loading' || m === 'error') return null;
  const cnt90   = Number(m.order_count_90       ?? 0);
  if (cnt90 >= 3) return 'high';
  if (cnt90 >= 1) return 'moderate';
  return 'low';
}

/**
 * The reorder point a row shows: the user-set value if there is one (a local
 * override saved this session wins over the loaded roLevel), otherwise the
 * recommendation. Shared by the table and the export so both agree.
 */
function reorderPointFor(item, m, roOverrides) {
  const effectiveRo = Object.prototype.hasOwnProperty.call(roOverrides, item.idCatalog)
    ? roOverrides[item.idCatalog]
    : item.roLevel;
  const isUserSet = effectiveRo != null && Number(effectiveRo) !== 0;
  const rec = computeRecommendedRo(m);
  const finalRo = isUserSet ? Number(effectiveRo) : (rec || 0);
  const availableVal = Number(item.availQty || 0);
  return { effectiveRo, isUserSet, rec, finalRo, availableVal, isBelowRo: finalRo > 0 && availableVal < finalRo };
}

function getConfidenceContext(m) {
  const cnt90      = Number(m.order_count_90       ?? 0);
  const cntLife    = Number(m.lifetime_order_count ?? 0);
  const level      = getConfidenceLevel(m);
  if (level === 'high') return null;
  if (level === 'moderate') {
    return `Only ${cnt90} outbound transaction${cnt90 !== 1 ? 's' : ''} in the last 90 days — metrics are directional.`;
  }
  // low
  if (cntLife === 0) return `No outbound transaction history — derived metrics cannot be calculated.`;
  return `No outbound transactions in the last 90 days (${cntLife} lifetime) — insufficient recent activity to calculate current velocity.`;
}

// ─── Component ──────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;
// Export reads the same endpoints in bigger steps: 500 is the list's maximum
// page, and 200 ids keep the metrics URL short.
const EXPORT_PAGE_SIZE = 500;
const EXPORT_METRICS_BATCH = 200;

function Catalog() {
  const { user } = useAuth();

  // Phase 1: core list (fast)
  const [catalog, setCatalog]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [hasMore, setHasMore]       = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Phase 2: metrics (lazy)
  // Map of idCatalog → metric row | 'loading' | 'error'
  const [metrics, setMetrics]       = useState({});

  // Detail modal
  const [selectedItem, setSelectedItem] = useState(null); // { idCatalog, itemNumber, itemName }

  // Reorder point: local optimistic overrides after save, plus edit-in-place state
  const [roOverrides, setRoOverrides] = useState({});
  const [editingRo,   setEditingRo]   = useState(null);  // idCatalog currently in edit mode
  const [editRoInput, setEditRoInput] = useState('');

  const [exporting, setExporting] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 500);
  const observerTarget  = useRef(null);
  // Track which idCatalog IDs have already been requested so we don't double-fetch
  const pendingMetrics  = useRef(new Set());

  // ── Phase 2: fetch metrics for a list of idCatalog IDs ──────────────────
  const fetchMetrics = useCallback(async (ids) => {
    const toFetch = ids.filter(id => !pendingMetrics.current.has(id));
    if (!toFetch.length) return;

    toFetch.forEach(id => pendingMetrics.current.add(id));

    // Mark as loading
    setMetrics(prev => {
      const next = { ...prev };
      toFetch.forEach(id => { if (!next[id]) next[id] = 'loading'; });
      return next;
    });

    const result = await getCatalogMetrics(toFetch);

    setMetrics(prev => {
      const next = { ...prev };
      toFetch.forEach(id => {
        next[id] = result.metrics?.[id] ?? 'error';
      });
      return next;
    });
  }, []);
  // ── Save a reorder point (0 = clear back to recommended) ───────────────────────────────
  const saveRoLevel = async (idCatalog, rawValue) => {
    const val = rawValue === '' ? 0 : Math.max(0, parseInt(rawValue, 10) || 0);
    // Optimistic update
    setRoOverrides(prev => ({ ...prev, [idCatalog]: val }));
    setEditingRo(null);
    try {
      await updateCatalogRoLevel(idCatalog, val);
    } catch (err) {
      console.error('Failed to save reorder point:', err);
      // Revert on failure
      setRoOverrides(prev => { const n = { ...prev }; delete n[idCatalog]; return n; });
      setError('Failed to save reorder point. Please try again.');
    }
  };
  // ── Phase 1: fetch core catalog page ────────────────────────────────────
  const fetchCatalog = useCallback(async (page, reset = false) => {
    if (!user?.customerId) return;
    try {
      if (reset) {
        setLoading(true);
        setError(null);
      }

      const response = await getCatalogItems(page, PAGE_SIZE, debouncedSearch);
      const items = response?.items || [];
      const pagination = response?.pagination || {};

      if (reset) {
        setCatalog(items);
        // Reset metric tracking on new search
        setMetrics({});
        pendingMetrics.current = new Set();
      } else {
        setCatalog(prev => [...prev, ...items]);
      }

      setHasMore(!!pagination.hasMore);
      setCurrentPage(page);

      // Fire Phase 2 immediately for this page's IDs
      const ids = items.map(r => r.idCatalog).filter(Boolean);
      if (ids.length) fetchMetrics(ids);
    } catch (err) {
      console.error('Error fetching catalog:', err);
      setError('Failed to load catalog. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.customerId, debouncedSearch, fetchMetrics]);

  // Reset + load page 1 when user or search changes
  useEffect(() => {
    if (!user) return;
    if (!user.customerId) {
      setLoading(false);
      setError('User ID not available. Please try logging in again.');
      return;
    }
    fetchCatalog(1, true);
  }, [user?.customerId, debouncedSearch]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchCatalog(currentPage + 1, false);
        }
      },
      { threshold: 0.1 }
    );
    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => { if (observerTarget.current) observer.unobserve(observerTarget.current); };
  }, [hasMore, loading, currentPage, fetchCatalog]);

  // ── Export ────────────────────────────────────────────────────────────────
  // One row per item with the page's own figures, for every item the current
  // search lists (not just the rows scrolled into view). It used to export
  // lots from the inventory endpoint, whose "turnover" is a different formula
  // (90-day issues ÷ on hand, not annualized), so the file disagreed with
  // the screen.
  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);

      // The api helpers return empty results instead of throwing, so check
      // that every row and every row's metrics arrived before writing a file.
      const items = [];
      let total = null;
      for (let page = 1; ; page++) {
        const response = await getCatalogItems(page, EXPORT_PAGE_SIZE, debouncedSearch);
        if (total === null) total = response.pagination.total;
        items.push(...response.items);
        if (!response.pagination.hasMore) break;
      }
      if (items.length !== total) throw new Error(`catalog list returned ${items.length} of ${total} items`);

      const allMetrics = {};
      for (let i = 0; i < items.length; i += EXPORT_METRICS_BATCH) {
        const ids = items.slice(i, i + EXPORT_METRICS_BATCH).map(r => r.idCatalog);
        const result = await getCatalogMetrics(ids);
        const got = ids.filter(id => result.metrics?.[id]);
        if (got.length !== ids.length) throw new Error(`metrics missing for ${ids.length - got.length} items`);
        Object.assign(allMetrics, result.metrics);
      }

      const num = (v) => (v == null ? null : Number(v));
      const exportData = items.map(item => {
        const m = allMetrics[item.idCatalog];
        const ro = reorderPointFor(item, m, roOverrides);
        const confidence = getConfidenceLevel(m);
        // Same rule as the table: with no orders in 90 days the velocity
        // metrics are not shown, so they are left blank here too.
        const velocity = (v) => (confidence === 'low' ? null : num(v));
        const avgAge = Number(m.weighted_avg_age ?? 0);
        return {
          'Item #':             item.itemNumber,
          'Item Name':          item.itemName,
          'Reorder Pt':         ro.finalRo || null,
          'Reorder Pt Source':  ro.isUserSet ? 'User-set' : (ro.rec ? 'Recommended' : ''),
          'Below Reorder Pt':   ro.isBelowRo ? 'Yes' : 'No',
          'Avg Age (days)':     avgAge > 0 ? avgAge : null,
          'Days Supply':        velocity(m.days_of_supply),
          'Turns/Yr':           velocity(m.turnover_annual),
          'Sell-Thru %':        confidence === 'low' ? null : Math.round(Number(m.sell_through_90 ?? 0) * 1000) / 10,
          'Avg Ord Qty':        num(m.avg_order_qty),
          'Avg Days/Ord':       velocity(m.avg_days_between_orders),
          'Orders (90d)':       Number(m.order_count_90 ?? 0),
          'Units Issued (90d)': Number(m.issued_90_days ?? 0),
          'Data Confidence':    { high: 'High', moderate: 'Moderate', low: 'Low data' }[confidence] ?? '',
          'TAP':                Number(item.tap || 0),
          'On Hand':            Number(item.onHand || 0),
          'Available':          Number(item.availQty || 0),
        };
      });

      // Loaded on demand: ExcelJS is large and only needed when someone
      // actually exports, so it stays out of the initial bundle.
      const { downloadWorkbook } = await import('../utils/excelExport');
      await downloadWorkbook(
        [{ name: 'Catalog', data: exportData, widths: [16, 40, 11, 17, 16, 14, 12, 10, 11, 12, 13, 13, 18, 16, 10, 10, 10] }],
        'catalog_export.xlsx'
      );
    } catch (err) {
      console.error('Error exporting catalog:', err);
      setError('Failed to export catalog data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderMetricCells = (idCatalog) => {
    const m = metrics[idCatalog];

    if (!m || m === 'loading') {
      return (
        <>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}><MetricSkeleton /></td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}><MetricSkeleton /></td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}><MetricSkeleton /></td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}><MetricSkeleton /></td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}><MetricSkeleton /></td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}><MetricSkeleton /></td>
        </>
      );
    }

    if (m === 'error') {
      return (
        <>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', color: '#6c757d' }}>—</td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', color: '#6c757d' }}>—</td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', color: '#6c757d' }}>—</td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', color: '#6c757d' }}>—</td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', color: '#6c757d' }}>—</td>
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', color: '#6c757d' }}>—</td>
        </>
      );
    }

    const turns      = Number(m.turnover_annual  ?? 0);
    const sellThru   = Number(m.sell_through_90  ?? 0);
    const dos        = m.days_of_supply != null ? Number(m.days_of_supply) : null;
    const avgAge     = Number(m.weighted_avg_age ?? 0);
    const avgOrdQty    = m.avg_order_qty           != null ? Number(m.avg_order_qty)           : null;
    const avgDaysBtwn  = m.avg_days_between_orders != null ? Number(m.avg_days_between_orders) : null;

    const confidence = getConfidenceLevel(m);
    const confContext = getConfidenceContext(m);

    // Low confidence: dim all velocity metrics and show a warning on the first cell
    if (confidence === 'low') {
      const dimStyle = { textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', color: '#adb5bd', fontStyle: 'italic' };
      const firstStyle = { ...dimStyle };
      return (
        <>
          {/* Avg Age — always meaningful regardless of velocity */}
          <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: avgAgeColor(avgAge) }}>
            {avgAge > 0 ? `${avgAge}d` : '—'}
          </td>
          {/* Days of supply */}
          <td style={{ ...dimStyle }} title={confContext}>—</td>
          {/* Turns/yr */}
          <td style={{ ...firstStyle, position: 'relative' }} title={confContext}>
            <span style={{ fontSize: '0.75rem' }}>Low data</span>
          </td>
          {/* Sell-thru */}
          <td style={dimStyle} title={confContext}>—</td>
          {/* Avg ord qty */}
          <td style={dimStyle} title={confContext}>{avgOrdQty != null ? avgOrdQty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
          {/* Avg days/ord */}
          <td style={dimStyle} title={confContext}>—</td>
        </>
      );
    }

    // Moderate confidence: show values but with a caution icon on the first velocity cell
    const moderateWarn = confidence === 'moderate';
    const warnIcon = moderateWarn
      ? <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginLeft: 4, fontSize: '0.65rem', color: '#fd7e14', opacity: 0.8 }} title={confContext} />
      : null;

    return (
      <>
        {/* Qty-weighted avg age */}
        <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: avgAgeColor(avgAge) }}>
          {avgAge > 0 ? `${avgAge}d` : '—'}
        </td>
        {/* Days of supply */}
        <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: daysOfSupplyColor(dos) }}
            title={moderateWarn
              ? confContext
              : dos != null ? `${Number(m.available_inventory).toLocaleString()} available ÷ (${Number(m.issued_90_days).toLocaleString()} issued ÷ 90 days)` : undefined}>
          {dos != null ? `${dos}d` : '—'}{moderateWarn && warnIcon}
        </td>
        {/* Annualized turns/yr */}
        <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: turnoverColor(turns) }}
            title={moderateWarn
              ? confContext
              : `Annualized: (${m.issued_90_days} issued × 4.06) ÷ avg inventory ${Math.round((Number(m.beginning_inventory) + Number(m.current_inventory)) / 2)}`}>
          {turns.toFixed(2)}×{moderateWarn && warnIcon}
        </td>
        {/* 90-day sell-through */}
        <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: sellThroughColor(sellThru) }}
            title={moderateWarn ? confContext : `${m.issued_90_days} issued ÷ (${m.issued_90_days} + ${m.current_inventory} on-hand)`}>
          {(sellThru * 100).toFixed(1)}%{moderateWarn && warnIcon}
        </td>
        {/* Average order quantity */}
        <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}
            title={moderateWarn
              ? confContext
              : avgOrdQty != null ? `${m.issued_90_days} units issued across ${m.order_count_90} orders in the last 90 days` : 'No issues in the last 90 days'}>
          {avgOrdQty != null ? avgOrdQty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}{moderateWarn && warnIcon}
        </td>
        {/* Average days between orders */}
        <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}
            title={moderateWarn
              ? confContext
              : avgDaysBtwn != null
                ? `${m.order_count_90} orders over ${m.order_span_days} days (first to last in the last 90) = ~${avgDaysBtwn}d apart`
                : m.order_count_90 === 1 ? '1 order in 90 days — need ≥2 to compute interval' : 'No orders in last 90 days'
            }>
          {avgDaysBtwn != null ? `${avgDaysBtwn}d` : '—'}{moderateWarn && warnIcon}
        </td>
      </>
    );
  };

  // ── Reorder point cell (click-to-edit, before Avg Age) ───────────────────
  const renderReorderCell = (item) => {
    const m = metrics[item.idCatalog];
    const { effectiveRo, isUserSet, rec, availableVal, isBelowRo } = reorderPointFor(item, m, roOverrides);

    const cellBase = { 
      textAlign: 'right', 
      padding: '6px 8px', 
      borderBottom: '1px solid #dee2e6',
      backgroundColor: isBelowRo ? '#ffe6e6' : 'transparent'
    };

    // ── Editing mode ──
    if (editingRo === item.idCatalog) {
      return (
        <td onClick={e => e.stopPropagation()} style={{ ...cellBase, padding: '3px 5px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
            <input
              autoFocus
              type="number"
              min="0"
              value={editRoInput}
              onChange={e => setEditRoInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter')  saveRoLevel(item.idCatalog, editRoInput);
                if (e.key === 'Escape') setEditingRo(null);
              }}
              style={{ width: 58, fontSize: '0.8rem', padding: '1px 3px', borderRadius: 3, border: '1px solid #86b7fe', textAlign: 'right' }}
            />
            <button onClick={() => saveRoLevel(item.idCatalog, editRoInput)} title="Save (Enter)"
              style={{ border: 'none', background: 'none', color: '#198754', cursor: 'pointer', padding: '0 2px', fontSize: '0.85rem' }}>
              <FontAwesomeIcon icon={faCheck} />
            </button>
            <button onClick={() => setEditingRo(null)} title="Cancel (Esc)"
              style={{ border: 'none', background: 'none', color: '#dc3545', cursor: 'pointer', padding: '0 2px', fontSize: '0.85rem' }}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </td>
      );
    }

    const startEdit = (e) => {
      e.stopPropagation();
      setEditRoInput(isUserSet ? String(effectiveRo) : (rec ? String(rec) : ''));
      setEditingRo(item.idCatalog);
    };

    // ── User-set value ──
    if (isUserSet) {
      return (
        <td onClick={startEdit} 
          title={isBelowRo 
            ? `User-set reorder point: ${Number(effectiveRo).toLocaleString()} · STOCK LOW! Only ${availableVal.toLocaleString()} available. Click to edit.`
            : `User-set reorder point · ${availableVal.toLocaleString()} available · Click to edit`}
          style={{ ...cellBase, cursor: 'pointer', color: isBelowRo ? '#dc3545' : '#1a56db', fontWeight: 600 }}>
          {Number(effectiveRo).toLocaleString()}
          <FontAwesomeIcon icon={faPencil} style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.45 }} />
        </td>
      );
    }

    // ── Not user-set: need metrics to show recommendation ──
    if (!m || m === 'loading') {
      return <td style={cellBase}><MetricSkeleton /></td>;
    }
    if (m === 'error') {
      return (
        <td onClick={startEdit} title="Click to set reorder point"
          style={{ ...cellBase, cursor: 'pointer', color: '#adb5bd' }}>—</td>
      );
    }

    return (
      <td onClick={startEdit}
        title={rec
          ? `Recommended: ~30 days of demand (${m.issued_90_days} units ÷ 90d × 30d)${isBelowRo ? ` · STOCK LOW! Only ${availableVal.toLocaleString()} available.` : ''} Click to override.`
          : 'No recent activity — click to set manually'}
        style={{ 
          ...cellBase, 
          cursor: 'pointer', 
          color: isBelowRo ? '#ba3c3c' : '#6c757d', 
          fontStyle: 'italic',
          fontWeight: isBelowRo ? 600 : 'normal'
        }}>
        {rec ? `~${rec.toLocaleString()}` : '—'}
        <FontAwesomeIcon icon={faPencil} style={{ marginLeft: 4, fontSize: '0.6rem', opacity: 0.35 }} />
      </td>
    );
  };

  // ── Initial spinner (before any rows) ────────────────────────────────────
  if (loading && catalog.length === 0) {
    return (
      <Container fluid className="p-4">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading…</span>
          </Spinner>
          <div className="mt-2 text-muted small">Loading catalog…</div>
        </div>
      </Container>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <Container fluid className="p-4">
      <Row className="mb-4 align-items-center">
        <Col md={3}>
          <h2 className="mb-0">Catalog</h2>
        </Col>
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search by item number or name…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={3} className="text-end">
          <Button variant="success" onClick={handleExport} className="me-2" disabled={exporting}>
            {exporting
              ? <Spinner animation="border" size="sm" className="me-2" />
              : <FontAwesomeIcon icon={faFileExcel} className="me-2" />}
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      <div style={{
        height: 'calc(100vh - 325px)',
        overflowY: 'auto',
        overflowX: 'auto',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        position: 'relative',
      }}>
        {/* Plain <table> — avoids Bootstrap's border-collapse:collapse class
             which silently breaks position:sticky on th elements */}
        <table style={{
          width: '100%',
          tableLayout: 'fixed',
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: '0.875rem',
        }}>
          <colgroup>
            <col style={{ width: '130px' }} />
            <col />
            {/* metric cols */}
            <col style={{ width: '95px' }} />{/* Reorder Pt */}
            <col style={{ width: '85px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '95px' }} />
            <col style={{ width: '95px' }} />
            {/* quantity cols */}
            <col style={{ width: '100px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '90px' }} />
          </colgroup>
          <thead>
            <tr>
              {[
                { label: 'Item #',       cls: '',          title: undefined },
                { label: 'Item Name',    cls: '',          title: undefined },
                { label: 'Reorder Pt',  cls: 'text-end',  title: 'Reorder point: blue/bold = user-set value, gray/italic = system recommendation (~30 days of demand). Click any cell to edit.' },
                { label: 'Avg Age',      cls: 'text-end',  title: 'Quantity-weighted average age of on-hand inventory since receipt' },
                { label: 'Days Supply',  cls: 'text-end',  title: 'Days of supply remaining at the 90-day average issue rate (available ÷ daily usage). Stock on hold or already on an order is not counted.' },
                { label: 'Turns/Yr',     cls: 'text-end',  title: 'Annualized inventory turns/year = (90d issues × 365/90) ÷ average inventory. Average = (beginning + ending) / 2, beginning reconstructed from transaction history.' },
                { label: 'Sell-Thru',    cls: 'text-end',  title: '90-day sell-through rate = units issued ÷ (units issued + units on hand).' },
                { label: 'Avg Ord Qty',  cls: 'text-end',  title: 'Average units per order over the last 90 days (total units issued ÷ number of issue transactions).' },
                { label: 'Avg Days/Ord', cls: 'text-end',  title: 'Average days between orders over the last 90 days (days from first to last order ÷ (orders − 1)). Requires ≥2 orders; shown as — otherwise.' },
                { label: 'TAP',          cls: 'text-end',  title: undefined },
                { label: 'On Hand',      cls: 'text-end',  title: undefined },
                { label: 'Available',    cls: 'text-end',  title: undefined },
              ].map(({ label, cls, title }) => (
                <th
                  key={label}
                  className={cls}
                  title={title}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    backgroundColor: '#f8f9fa',
                    /* border-collapse:separate breaks normal borders on sticky th;
                       use box-shadow to fake the bottom border instead */
                    boxShadow: 'inset 0 -2px 0 #dee2e6, inset 0 1px 0 #dee2e6',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {catalog.map((item, i) => (
              <tr key={item.idCatalog}
                style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f8f9fa', cursor: 'pointer' }}
                onClick={() => setSelectedItem(item)}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e8f4fd'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = i % 2 === 0 ? '#fff' : '#f8f9fa'}
              >
                <td style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}>{item.itemNumber}</td>
                <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}>{item.itemName}</td>
                {renderReorderCell(item)}
                {renderMetricCells(item.idCatalog)}
                <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}>{Number(item.tap || 0).toLocaleString()}</td>
                <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}>{Number(item.onHand || 0).toLocaleString()}</td>
                {/* Nothing available is flagged: out of stock (listed because
                    of recent demand or a reorder point), or all of it held */}
                {Number(item.availQty || 0) <= 0 ? (
                  <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6', backgroundColor: '#ffe6e6', color: '#dc3545', fontWeight: 600 }}
                      title={Number(item.onHand || 0) > 0 ? 'None available: all on-hand stock is on hold or on an order' : 'Out of stock'}>
                    {Number(item.availQty || 0).toLocaleString()}
                  </td>
                ) : (
                  <td style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #dee2e6' }}>{Number(item.availQty || 0).toLocaleString()}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Infinite scroll sentinel */}
        <div ref={observerTarget} style={{ height: '20px', width: '100%' }}>
          {loading && catalog.length > 0 && (
            <div className="text-center py-2">
              <Spinner animation="border" size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* Detail modal */}
      {selectedItem && (
        <CatalogItemModal
          item={selectedItem}
          metrics={metrics[selectedItem.idCatalog]}
          onHide={() => setSelectedItem(null)}
        />
      )}

      {/* Metric legend */}
      <div className="mt-2 d-flex gap-3 flex-wrap" style={{ fontSize: '0.75rem', color: '#6c757d' }}>
        <span><strong>Reorder Pt</strong>: <span style={{ color: '#1a56db', fontWeight: 600 }}>blue/bold</span> = user-set &nbsp;·&nbsp; <span style={{ fontStyle: 'italic' }}>~italic</span> = recommended (~30d demand). Click to edit.</span>
        <span><strong>Turns/Yr</strong>: annualized inventory turns (≥4 good, 1–4 moderate, &lt;1 low)</span>
        <span><strong>Sell-Thru</strong>: 90-day sell-through rate (≥50% good, 20–50% moderate, &lt;20% low)</span>
        <span><strong>Days Supply</strong>: days until available stock runs out at the current rate (≥60 good, 30–60 watch, &lt;30 risk)</span>
        <span><strong>Avg Age</strong>: qty-weighted days since receipt (&gt;180d risk, &gt;90d watch)</span>
        <span><strong>Avg Ord Qty</strong>: avg units per issue transaction over last 90 days</span>
        <span><strong>Avg Days/Ord</strong>: avg days between orders, first to last order in the 90d window (≥2 orders required)</span>
        <span><strong>Available</strong>: <span style={{ color: '#dc3545', fontWeight: 600 }}>red</span> = none available. Out-of-stock items stay listed while they have recent orders or a reorder point.</span>
        <span>
          <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fd7e14', marginRight: 3 }} />
          <strong>Data confidence</strong>:
          &nbsp;≥3 orders/90d = reliable · 1–2 = caution (shown with ⚠) · 0 = insufficient (“Low data”, metrics hidden)
        </span>
      </div>

      {/* Disclaimer */}
      <div className="mt-3 p-3" style={{
        fontSize: '0.72rem',
        color: '#6c757d',
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        lineHeight: '1.5'
      }}>
        <strong>Disclaimer:</strong> Actual on-hand inventory counts are provided as-is. However, all derived
        analytics, performance metrics, and sell-through calculations are statistical models and estimates only.
        [Company Name] makes no guarantees regarding future inventory velocity or performance based on these
        metrics. Users must independently verify trends before making purchasing or ordering commitments.
      </div>
    </Container>
  );
}

export default Catalog; 